import React, { useState, useCallback } from 'react';
import { Plus, Star, LayoutList, Kanban } from 'lucide-react';

function ViewTabs({
  views,
  activeViewId,
  onSelectView,
  onCreateView,
  onReorderViews,
  onTogglePin,
}) {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image semi-transparent
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const newViews = [...views];
      const [moved] = newViews.splice(dragIndex, 1);
      newViews.splice(dragOverIndex, 0, moved);
      onReorderViews?.(newViews);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  // Sort: pinned first, then by position
  const sortedViews = [...views].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return 0;
  });

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
      <div className="flex items-center">
        <div className="flex items-center space-x-0.5 overflow-x-auto flex-1">
          {sortedViews.map((view, index) => {
            const isActive = activeViewId === view.id;
            const isDragTarget = dragOverIndex === index && dragIndex !== index;
            const ViewIcon = view.view_type === 'kanban' ? Kanban : LayoutList;

            return (
              <div
                key={view.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                className={`relative group flex-shrink-0 ${
                  isDragTarget ? 'border-l-2 border-brand-500' : ''
                }`}
              >
                <button
                  onClick={() => onSelectView(view.id)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-grab active:cursor-grabbing ${
                    isActive
                      ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <ViewIcon className="w-3 h-3 flex-shrink-0 opacity-50" />
                  <span>{view.name}</span>
                  {view.is_pinned && (
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400 flex-shrink-0" />
                  )}
                </button>

                {/* Pin button — visible on hover for non-pinned, always for pinned */}
                {onTogglePin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onTogglePin(view.id); }}
                    className={`absolute -top-1 -right-1 p-0.5 rounded-full transition-all ${
                      view.is_pinned
                        ? 'opacity-0 group-hover:opacity-100 bg-white border border-gray-200 shadow-sm text-amber-400 hover:text-gray-400'
                        : 'opacity-0 group-hover:opacity-100 bg-white border border-gray-200 shadow-sm text-gray-400 hover:text-amber-400'
                    }`}
                    title={view.is_pinned ? 'Unpin view' : 'Pin view'}
                  >
                    <Star className={`w-2.5 h-2.5 ${view.is_pinned ? 'fill-current' : ''}`} />
                  </button>
                )}
              </div>
            );
          })}

          {/* Add new view button */}
          <button
            onClick={onCreateView}
            className="px-3 py-2 text-sm font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded transition-colors flex items-center gap-1 flex-shrink-0 dark:text-brand-400 dark:hover:bg-brand-900/30"
            title="Create new view"
          >
            <Plus className="w-4 h-4" />
            <span>New View</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewTabs;
